import React, { ReactNode } from 'react';
import { useFormContext, useFieldArray, useController } from 'react-hook-form';

import { Button, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { TitleWithDescription, LbFormControlLabel } from '@infoods/lifebase-ui';

import { InfoBox } from '../../../../shared/components/ui-components/InfoBox/InfoBox';
import { ChallengeFormValues } from '../../../interfaces/ChallengeFormValues';
import { Mode } from '../../../../general-types';

import classes from './PrizesFrom.module.scss';
import { EditPrizeRow } from './EditPrizeRow/EditPrizeRow';
import { ViewPrizeRow } from './ViewPrizeRow/ViewPrizeRow';

type PrizesFromProps = {
  mode: Mode;
  headerActions?: ReactNode;
};

export const PrizesFrom = ({ mode, headerActions }: PrizesFromProps) => {
  const isViewMode = mode === 'VIEW';
  const { control } = useFormContext<ChallengeFormValues>();

  const {
    field: { value: showPrizes, onChange, onBlur, ref, name: fieldName },
  } = useController<any>({
    control,
    name: 'prizes.enabled',
    defaultValue: false,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'prizes.prizes',
  });

  const handleAddPrize = () => {
    append({ title: '', description: '', prizeNum: fields.length + 1 });
  };

  const handleRemovePrize = (index: number) => {
    remove(index);
  };

  const onSwitchChange = (_: any, checked: boolean) => {
    if (fields.length === 0) {
      handleAddPrize();
    }

    onChange(checked);
  };

  const SwitchTitle = (
    <LbFormControlLabel
      control={
        <Switch
          checked={showPrizes}
          onChange={onSwitchChange}
          slotProps={{ input: { ref } }}
          name={fieldName}
          onBlur={onBlur}
          disabled={isViewMode}
        />
      }
      label={
        <TitleWithDescription
          title="Prizes"
          description="Do you want to reward the champions? Add prizes to boost motivation, engagement, and commitment throughout the Challenge."
        />
      }
      align="start"
    />
  );

  return (
    <InfoBox
      title={SwitchTitle}
      icon={headerActions}
      hideConfig={{ show: showPrizes }}
      customContentPadding
    >
      {isViewMode ? (
        fields.map((field) => (
          <ViewPrizeRow
            key={field.id}
            title={field.title}
            description={field.description}
          />
        ))
      ) : (
        <>
          {fields.map((field, index) => (
            <EditPrizeRow
              key={field.id}
              index={index}
              control={control}
              onRemove={handleRemovePrize}
              showRemoveButton={fields.length > 1}
            />
          ))}
          <div className={classes.addButtonRow}>
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddPrize}
            >
              Add Prize
            </Button>
          </div>
        </>
      )}
    </InfoBox>
  );
};
